import { yupResolver } from "@hookform/resolvers/yup";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import styled, { keyframes } from "styled-components/macro";
import { useRecommendation } from "../contexts/recommendation-context";
import {
  createSchema,
  mathcesCondition,
  updateAnswer,
  useQuestion,
} from "../utils/question";
import {
  amountToNumber,
  Dropdown,
  NumberField,
  RadioButton,
  TextField,
} from "./formFields";
import * as colors from "../styles/colors";
import { useMutation } from "react-query";
import { BiLoaderAlt } from "react-icons/bi";
import { range } from "../utils/helpers";

import * as mq from "../styles/mediaQueries";

const backendStyleMap = {
  gray: colors.font2,
  blue: colors.primary,
  sm: "0.8em",
  m: "0.7em",
};

const childAgeQuestions = ["child1_age", "child2_age", "child3_age"];

const childFeesQuestions = ["child1_fees", "child2_fees", "child3_fees"];

function isChildQestion(question) {
  const { name } = question;

  const isChild =
    childAgeQuestions.includes(name) || childFeesQuestions.includes(name);

  return isChild;
}

function isChildFeesQuestion(question) {
  const { name } = question;
  const isChildFees = childFeesQuestions.includes(name);

  return isChildFees;
}

const superScripts = ["st", "nd", "rd", "th"];

function QuestionLabel({ question }) {
  const { name, question: label, styling } = question;
  return (
    <label
      htmlFor={name}
      css={`
        /* font-size: 2.17em; */
        font-size: ${styling?.display === "horizontal" ? "1em" : "1.9em"};
        margin-bottom: ${styling?.display === "horizontal" ? "1.6em" : "-10px"};
        margin-top: ${styling?.display === "horizontal" ? "2em" : "0"};
        margin-right: ${styling?.display === "horizontal" ? "2em" : "0"};
        color: ${colors.font1};
        text-align: center;

        ${mq.sm} {
          font-size: 1.2em;
          width: 90%;
          margin: auto;
          margin-bottom: ${styling?.display === "horizontal" ? "-1em" : "auto"};
        }
      `}
    >
      {label.split("\\n").map(line => {
        return (
          <div key={line}>
            {line.split(" ").map(word => {
              const styles = {
                color: colors.font1,
                fontSize: "inherit",
              };
              if (styling) {
                const { highlight, color, font_size } = styling;
                if (
                  highlight &&
                  highlight.toLowerCase() ===
                    word.replace("\n", "").replace("?", "").toLowerCase()
                ) {
                  if (color) styles.color = backendStyleMap[color];
                  if (font_size) styles.fontSize = backendStyleMap[font_size];
                  return <span style={styles}>{word} </span>;
                }
              }
              if (
                superScripts.some(superScript => word.includes(superScript)) &&
                isChildQestion(question)
              ) {
                return (
                  <span>
                    {word.substr(0, word.length - 2)}
                    <sup>{word.substr(word.length - 2, 2)}</sup>{" "}
                  </span>
                );
              }
              return word + " ";
            })}
          </div>
        );
      })}
      {question.instruction ? (
        <InfoText>({question.instruction})</InfoText>
      ) : null}
    </label>
  );
}

function questionType(type) {
  return function QuestionField({
    question,
    register = {},
    autoFocus,
    control,
    handleDropdownChange = () => {},
    shouldSubmit,
    ...props
  }) {
    const { name, validation } = question;
    const { answers, icons } = useRecommendation();

    const { logos } = question;

    let maxLength = 33;

    if (validation && validation?.max_length) {
      maxLength = validation?.max_length;
    }

    const isName = validation && validation.pattern === "full_name";

    switch (type) {
      case "text": {
        let iconSrc;
        if (logos) {
          iconSrc = icons ? icons[Object.keys(logos)[0]] : "";
        }
        return (
          <TextField
            autoFocus={autoFocus}
            defaultValue={answers[name]}
            id={name}
            iconSrc={iconSrc}
            decoration={isName ? "fullName" : ""}
            {...register(name)}
            {...props}
          />
        );
      }
      case "number": {
        let iconSrc;
        if (logos) {
          iconSrc = icons ? icons[Object.keys(logos)[0]] : "";
        }
        return (
          <NumberField
            defaultValue={answers[name]}
            autoFocus={autoFocus}
            id={name}
            iconSrc={iconSrc}
            // maxLength={label && label.match(/age/i) ? 2 : 33}
            maxLength={maxLength}
            size={isChildQestion(question) ? "md" : "lg"}
            {...register(name)}
            {...props}
          />
        );
      }

      case "amount": {
        let iconSrc;
        if (logos) {
          iconSrc = icons ? icons[Object.keys(logos)[0]] : "";
        }
        return (
          <NumberField
            defaultValue={answers[name]}
            autoFocus={autoFocus}
            id={name}
            iconSrc={iconSrc}
            amount
            // maxLength={tab === "expenses" ? 12 : 20}
            maxLength={maxLength}
            size={isChildQestion(question) ? "md" : "lg"}
            {...register(name)}
            {...props}
          />
        );
      }

      case "toggle": {
        const options = Object.keys(question.options);
        return (
          <div
            css={`
              display: flex;
              gap: 1em;
              margin-top: 1.6em;
              margin-bottom: 3.3em;

              ${mq.sm} {
                flex-direction: column;
              }
            `}
          >
            {options.map((option, idx) => (
              <RadioButton
                autoFocus
                name={question.name}
                id={question.name + option}
                value={option}
                key={option}
                defaultChecked={answers[name] === option}
                iconSrc={
                  logos ? (icons ? icons[Object.keys(logos)[idx]] : "") : ""
                }
                {...register(name)}
                {...props}
              >
                {question.options[option]}
              </RadioButton>
            ))}
          </div>
        );
      }

      case "age": {
        const { min, max } = validation;
        return (
          <div
            css={`
              margin-top: 3em;
            `}
          >
            <Controller
              render={({ field: { onChange, ...field } }) => (
                <Dropdown
                  options={range(min, max)}
                  autoFocus
                  {...field}
                  {...props}
                  defaultValue={answers[name] || "select"}
                  onChange={val => {
                    handleDropdownChange({ [name]: val });
                    onChange(val);
                  }}
                />
              )}
              control={control}
              name={name}
              defaultValue={answers[name] || "select"}
            />
          </div>
        );
      }

      default:
        return () => null;
    }
  };
}

function QuestionForm({ question, ...props }) {
  const { id, tab, enquiryId } = useParams();
  const { name, type, styling } = question;

  const { setAnswers, answers, questions } = useRecommendation();

  const { getSubQuestions, findNameBySequence } = useQuestion();

  const [subQuestions, setSubQuestions] = React.useState([]);

  React.useEffect(() => {
    const subQuestions = getSubQuestions(question);
    const shouldRender = subQuestion => {
      const { when } = subQuestion;
      if (!when) return true;
      return answers[findNameBySequence(when.question_id)] === when.option_name;
    };
    setSubQuestions(
      subQuestions.map(subQuestion => ({
        ...subQuestion,
        shouldRender: shouldRender(subQuestion),
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, questions]);

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(
      createSchema(
        [
          question,
          ...getSubQuestions(question).map(subQuestion => {
            if (!subQuestion.when) return subQuestion;
            return {
              ...subQuestion,
              when: subQuestion.when.map(when => ({
                option_name: when[1],
                question_id: findNameBySequence(when[0]),
              })),
            };
          }),
        ],
        answers,
      ),
    ),
    defaultValues: [question, ...getSubQuestions(question)].reduce(
      (defaultAnswers, question) => ({
        ...defaultAnswers,
        [question.name]: answers[question.name],
      }),
      {},
    ),
  });

  if (process.env.NODE_ENV === "development") console.log({ errors });

  const { mutateAsync: mutateUpdateAnswer, isLoading } =
    useMutation(updateAnswer);

  const mainQuestionAnswer = watch(question.name);

  const onSubmit = data => {
    const answer = data[name];

    if (!answer) return;

    const { last_question } = question;

    const sendData = Object.keys(data).reduce((sendData, name) => {
      const thisQuestion = questions[tab].find(que => que.name === name);
      if (thisQuestion && thisQuestion.type === "amount") {
        return { ...sendData, [name]: amountToNumber(data[name]) };
      }
      return { ...sendData, [name]: data[name] };
    }, {});

    mutateUpdateAnswer({
      ...answers,
      ...sendData,
      last_question,
      enquiryId,
    }).then(() => {
      setAnswers({ ...answers, ...sendData });
    });
  };

  const QuestionType = React.useMemo(
    () => questionType(type.trim()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [question],
  );

  const isPreviousQuestion = question_id => question_id !== question.sequence;

  function shouldRenderSubQuestion(subQuestion) {
    function checkCondition(condition) {
      const [question_id, conditionToCheck] = condition;
      let answerGiven = mainQuestionAnswer;
      if (isPreviousQuestion(question_id)) {
        const question_name = findNameBySequence(question_id);
        answerGiven = answers[question_name];
      }
      const doesMatch = mathcesCondition(conditionToCheck, answerGiven);
      return doesMatch;
    }
    const { when } = subQuestion;

    if (when) return when.every(checkCondition);

    return true;
  }

  const isToggleType = question =>
    !shouldRenderSubQuestion &&
    question.type &&
    ["toggle", "age"].includes(question.type.trim());

  const filteredSubQuestions = subQuestions.filter(shouldRenderSubQuestion);
  const bottomRef = React.useRef(null);

  if (Object.keys(errors).length > 0 && bottomRef.current) {
    setTimeout(() => {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }, 0);
  }

  const hideNextButton =
    isToggleType(question) && !answers[name] && subQuestions.length === 0;

  const submitOnChange =
    isToggleType(question) &&
    subQuestions.map(subQuestion => isToggleType(subQuestion));

  return (
    <div
      css={`
        margin-top: 3em;
      `}
    >
      <form
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        onChange={evt => {
          if (submitOnChange) {
            handleSubmit(onSubmit)(evt);
          }
        }}
        css={`
          display: flex;
          flex-flow: column;
          max-width: 37.6em;
          margin: auto;
          align-items: center;
          gap: 1em;
        `}
        {...props}
      >
        {isChildFeesQuestion(question) && (
          <QuestionLabel
            question={{
              question: `How much do you spend or plan on spending on your Child's education per year?`,
            }}
          />
        )}
        <div
          css={`
            display: flex;
            flex-flow: ${styling?.display === "horizontal" ? "row" : "column"};
            max-width: 37.6em;
            margin: auto;
            align-items: center;

            ${mq.sm} {
              flex-direction: column;
            }
          `}
        >
          <QuestionLabel question={question} />
          <div
            css={`
              position: relative;
              margin-bottom: 1.6em;
            `}
          >
            <QuestionType
              question={question}
              register={register}
              autoFocus
              isError={!!errors[name]}
              control={control}
              handleDropdownChange={
                subQuestions.length === 0 ? onSubmit : () => {}
              }
            />
            {errors[name] ? (
              <p
                css={`
                  margin-top: 0.3em;
                  color: red;
                  position: absolute;
                  top: 100%;
                  left: 0;
                  width: 100%;
                  text-align: center;
                `}
              >
                {errors[name].message}
              </p>
            ) : null}
          </div>
        </div>
        {filteredSubQuestions.map(subQuestion => {
          const QuestionType = questionType(subQuestion.type.trim());
          console.log(subQuestion.type.trim());
          return (
            <SubQuestion key={subQuestion.name} subQuestion={subQuestion}>
              <div
                css={`
                  position: relative;
                  margin-bottom: 1.6em;
                `}
              >
                <QuestionType
                  question={subQuestion}
                  register={register}
                  autoFocus={
                    !["text", "number", "amount"].includes(question.type.trim())
                  }
                  isError={!!errors[subQuestion.name]}
                  control={control}
                />
                {errors[subQuestion.name] && (
                  <p
                    css={`
                      margin-top: 0.3em;
                      color: red;
                      position: absolute;
                      text-align: center;
                      width: 100%;
                    `}
                  >
                    {errors[subQuestion.name].message}
                  </p>
                )}
              </div>
            </SubQuestion>
          );
        })}
        {hideNextButton ? null : (
          <NextButton type="submit" disabled={isLoading}>
            Next{" "}
            {isLoading ? (
              <BiLoaderAlt
                css={`
                  margin-left: 1em;
                  animation: ${rotate} 0.37s linear infinite;
                `}
              />
            ) : (
              ">"
            )}
          </NextButton>
        )}
      </form>
      <div
        ref={bottomRef}
        css={`
          margin-top: 2em;
        `}
      />
    </div>
  );
}

export const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

export { QuestionForm };

function SubQuestion({ subQuestion, children, ...props }) {
  const { styling } = subQuestion;
  return (
    <div
      css={`
        display: flex;
        flex-flow: ${styling?.display === "horizontal" ? "row" : "column"};
        max-width: 37.6em;
        margin: auto;
        align-items: center;
        display: relative;

        ${mq.sm} {
          flex-direction: column;
        }
      `}
      {...props}
    >
      <QuestionLabel question={subQuestion} />
      {children}
    </div>
  );
}

export const NextButton = styled.button`
  width: 10em;
  height: 3.3em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0182ff;
  font-weight: bold;
  color: white;
  border: 2px solid #0182ff;
  cursor: pointer;
  border-radius: 6px;
  font-size: 1.2em;
  font-family: monospace;
  transition: background-color 0.3s;
  position: fixed;
  bottom: 3em;
  left: 50%;
  transform: translate(-50%);

  &:disabled {
    opacity: 0.6;
  }

  &:hover {
    background-color: #fff;
    border: 2px solid #0182ff;
    color: #0182ff;
  }

  ${mq.sm} {
    position: static;
    margin: auto;
    margin-top: 6em;
    transform: none;
    width: 90%;
    font-size: 1em;
  }
`;

const InfoText = styled.p`
  color: #8a919c;
  font-size: 1rem;
  margin-top: 1.1em;
`;
